import { useRef } from 'react';
import SectionHeader from '~/components/common/SectionHeader';
import {
  ChakraBox,
  ChakraFlex,
  coalescePlainTextToEditorV2Html,
  DateFormat,
  EditorV2State,
  FAIconName,
  Icon,
  SectionCard,
  SkeletonLoader,
  Tag,
  Text,
  TooltipIcon,
  ValidationMessage,
} from '@equip.health/ui';
import { GetDischargePlanResponse } from '~/store/services/clinical-data/dischargePlan';
import { useDischargePlanViewContext } from '../../DischargePlanViewContext';
import { IsPatientSeekingCares } from '../../common/dischargePlanConstants';
import { DateTime } from 'luxon';
import { ValidationType } from '~/lib/constants/enumeration.constants';
import ValueOrNotSet from '../../common/LabelOrNotSet';
import EditorV2ValueOrNotSet from '../../common/EditorV2OrNotSet';
import TagListOrNotSet from '../../common/TagListOrNotSet';
import { getDateTagProps } from '~/lib/util/date.util';
import { useGetSecondaryReasonsQuery } from '~/store/services/clinical-data/refDischargePlan';

interface DischargePlanDetailsSectionProps {
  showSkeleton: boolean;
  dischargePlanData?: GetDischargePlanResponse;
}

export const DischargeDetailsSectionCard = ({
  showSkeleton,
  dischargePlanData,
}: DischargePlanDetailsSectionProps) => {
  const additionalDetailsEditorRef = useRef<EditorV2State>(null);
  const statusAtDischargeEditorRef = useRef<EditorV2State>(null);
  const {
    errors: { dischargeDetails: errors },
  } = useDischargePlanViewContext();

  const { data: secondaryReasonsData } = useGetSecondaryReasonsQuery();

  // Check if primary has associated secondary reasons
  const primaryHasSecondaryReasons =
    secondaryReasonsData?.some(
      (sr) =>
        sr.primaryDischargeReason?.statusReasonExternalId ===
        dischargePlanData?.primaryDischargeReason?.statusReasonExternalId,
    ) ?? false;

  if (showSkeleton) {
    return (
      <SkeletonLoader
        height="150px"
        id="discharge-details-skeleton"
        width="100%"
      />
    );
  }

  const htmlAdditionalDetails: string | null =
    dischargePlanData?.additionalDischargeReasonDetails
      ? coalescePlainTextToEditorV2Html(
          dischargePlanData.additionalDischargeReasonDetails,
        )
      : null;

  const htmlStatusAtDischarge: string | null =
    dischargePlanData?.statusAtDischarge
      ? coalescePlainTextToEditorV2Html(dischargePlanData.statusAtDischarge)
      : null;

  const isPatientSeekingCare =
    dischargePlanData?.isPatientSeekingCare?.code ===
    IsPatientSeekingCares.YES.code;

  const tentativeDischargeDateTagProps = getDateTagProps(
    dischargePlanData?.tentativeDischargeDate,
  );

  return (
    <SectionCard id="discharge-details">
      <ChakraFlex flexDirection="column" gap="24px">
        <ChakraFlex alignItems="baseline" flexDirection="row">
          <ChakraFlex flexDirection="inherit" gap="6px" minWidth="250px">
            <Text
              color="neutral.secondary"
              id="tentative-discharge-date-label"
              variant="caption"
            >
              Tentative discharge date
            </Text>
            <TooltipIcon
              iconColor="neutral.secondary"
              iconName={FAIconName.infoCircleRegular}
              id="tentative-discharge-date"
              placement="bottom-start"
              tooltipText="The tentative discharge date is an estimate to help the care team prepare for an upcoming discharge.
                  At discharge, the actual discharge date will be set to the date of the patient's last session with Equip."
              width="300px"
            />
          </ChakraFlex>
          <ChakraFlex
            alignItems="center"
            aria-labelledby="tentative-discharge-date-label"
            flexDirection="row"
            gap="6px"
          >
            {dischargePlanData?.tentativeDischargeDate ? (
              <>
                <Icon
                  aria-label={FAIconName.calendarRegular}
                  name={FAIconName.calendarRegular}
                  color="neutral.secondary"
                />
                <Text>
                  {DateTime.fromISO(
                    dischargePlanData?.tentativeDischargeDate,
                  ).toFormat(DateFormat.longDate)}
                </Text>
                {tentativeDischargeDateTagProps && (
                  <Tag {...tentativeDischargeDateTagProps} />
                )}
              </>
            ) : (
              <ChakraFlex flexDirection="column" gap="6px">
                <Text color="neutral.tertiary">Not set</Text>
                {errors.tentativeDischargeDate && (
                  <ValidationMessage
                    id="tentative-discharge-date"
                    role="alert"
                    type={ValidationType.CRITICAL}
                    value="Tentative discharge date is required"
                  />
                )}
              </ChakraFlex>
            )}
          </ChakraFlex>
        </ChakraFlex>
        <ChakraFlex alignItems="baseline" flexDirection="row">
          <ChakraBox minWidth="250px">
            <Text
              color="neutral.secondary"
              id="initiated-by-label"
              variant="caption"
            >
              Initiated by
            </Text>
          </ChakraBox>
          <ChakraFlex flexDirection="column" gap="6px">
            <ValueOrNotSet
              aria-labelledby="initiated-by-label"
              value={dischargePlanData?.whoInitiatedDischarge?.label}
            />
            {errors.whoInitiatedDischarge && (
              <ValidationMessage
                id="initiated-by"
                role="alert"
                type={ValidationType.CRITICAL}
                value="Initiated by is required"
              />
            )}
          </ChakraFlex>
        </ChakraFlex>

        <SectionHeader headerText="Reason for discharge" variant="bodyMedium" />
        <ChakraFlex alignItems="baseline" flexDirection="row">
          <ChakraBox minWidth="250px">
            <Text
              color="neutral.secondary"
              id="primary-discharge-reason-label"
              variant="caption"
            >
              Primary discharge reason
            </Text>
          </ChakraBox>
          <ChakraFlex flexDirection="column" gap="6px">
            <ValueOrNotSet
              aria-labelledby="primary-discharge-reason-label"
              value={
                dischargePlanData?.primaryDischargeReason?.statusReasonName
              }
            />
            {errors.primaryDischargeReason && (
              <ValidationMessage
                id="primary-discharge-reason"
                role="alert"
                type={ValidationType.CRITICAL}
                value="Primary discharge reason is required"
              />
            )}
          </ChakraFlex>
        </ChakraFlex>
        {(dischargePlanData?.secondaryDischargeReason ||
          primaryHasSecondaryReasons) && (
          <ChakraFlex alignItems="baseline" flexDirection="row">
            <ChakraBox minWidth="250px">
              <Text
                color="neutral.secondary"
                id="secondary-discharge-reason-label"
                variant="caption"
              >
                Secondary discharge reason
              </Text>
            </ChakraBox>
            <ChakraFlex flexDirection="column" gap="6px">
              <ValueOrNotSet
                aria-labelledby="secondary-discharge-reason-label"
                value={dischargePlanData?.secondaryDischargeReason?.label}
              />
              {errors.secondaryDischargeReason && (
                <ValidationMessage
                  id="secondary-discharge-reason"
                  role="alert"
                  type={ValidationType.CRITICAL}
                  value="Secondary discharge reason is required"
                />
              )}
            </ChakraFlex>
          </ChakraFlex>
        )}
        <ChakraFlex flexDirection="column" gap="6px">
          <Text
            color="neutral.secondary"
            id="additional-details-label"
            variant="caption"
          >
            Additional details
          </Text>

          <EditorV2ValueOrNotSet
            aria-labelledby="additional-details-label"
            containerTestId="additional-discharge-reason-details"
            hideBorder
            initialContent={htmlAdditionalDetails}
            key={htmlAdditionalDetails}
            readOnly
            stateRef={additionalDetailsEditorRef}
          />
        </ChakraFlex>

        <SectionHeader
          headerText="Patient care post-Equip"
          variant="bodyMedium"
        />
        <ChakraFlex alignItems="baseline" flexDirection="row">
          <ChakraBox minWidth="250px">
            <Text
              color="neutral.secondary"
              id="seeking-care-post-equip-label"
              variant="caption"
            >
              Seeking care post-Equip
            </Text>
          </ChakraBox>
          <ChakraFlex flexDirection="column" gap="6px">
            <ValueOrNotSet
              aria-labelledby="seeking-care-post-equip-label"
              value={dischargePlanData?.isPatientSeekingCare?.label}
            />
            {errors.isPatientSeekingCare && (
              <ValidationMessage
                id="seeking-care-post-equip"
                role="alert"
                type={ValidationType.CRITICAL}
                value="Seeking care post-Equip is required"
              />
            )}
          </ChakraFlex>
        </ChakraFlex>
        {isPatientSeekingCare && (
          <ChakraFlex alignItems="baseline" flexDirection="row">
            <ChakraBox minWidth="250px">
              <Text
                color="neutral.secondary"
                id="type-of-care-label"
                variant="caption"
              >
                Type of care
              </Text>
            </ChakraBox>
            <ChakraFlex flexDirection="column" gap="6px">
              <TagListOrNotSet
                aria-labelledby="type-of-care-label"
                id="type-of-care"
                tags={dischargePlanData?.typeOfCare?.map((toc) => toc.label)}
              />
              {errors.typeOfCare && (
                <ValidationMessage
                  id="type-of-care"
                  role="alert"
                  type={ValidationType.CRITICAL}
                  value="Type of care is required"
                />
              )}
            </ChakraFlex>
          </ChakraFlex>
        )}

        {isPatientSeekingCare && (
          <ChakraFlex alignItems="baseline" flexDirection="row">
            <ChakraBox minWidth="250px">
              <Text
                color="neutral.secondary"
                id="level-of-care-label"
                variant="caption"
              >
                Level of care
              </Text>
            </ChakraBox>
            <ChakraFlex flexDirection="column" gap="6px">
              <TagListOrNotSet
                aria-labelledby="level-of-care-label"
                id="level-of-care"
                tags={dischargePlanData?.levelOfCare?.map((loc) => loc.label)}
              />
              {errors.levelOfCare && (
                <ValidationMessage
                  id="level-of-care"
                  role="alert"
                  type={ValidationType.CRITICAL}
                  value="Level of care is required"
                />
              )}
            </ChakraFlex>
          </ChakraFlex>
        )}

        <SectionHeader headerText="Patient status" variant="bodyMedium" />
        <ChakraFlex flexDirection="column" gap="6px">
          <Text
            color="neutral.secondary"
            id="patient-status-at-discharge-label"
            variant="caption"
          >
            Patient status at discharge
          </Text>
          <ChakraBox>
            <EditorV2ValueOrNotSet
              aria-labelledby="patient-status-at-discharge-label"
              containerTestId="patient-status-at-discharge"
              hideBorder
              initialContent={htmlStatusAtDischarge}
              key={htmlStatusAtDischarge}
              readOnly
              stateRef={statusAtDischargeEditorRef}
            />
          </ChakraBox>
        </ChakraFlex>
      </ChakraFlex>
    </SectionCard>
  );
};
